import type {
  SupportChannel,
  SupportEvent,
  SupportTicketPriority,
  SupportTicketStatus,
  SupportTicketType,
  SupportViewKey,
} from "../../shared/support-schemas";

// ADR-0089 and ADR-0087: the support desk's words, in the terms employees use.

type BadgeVariant = "default" | "secondary" | "success" | "warning" | "destructive";

export const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  new: "New",
  open: "Open",
  waiting_on_customer: "Waiting on customer",
  on_hold: "On hold",
  solved: "Solved",
};

export const STATUS_BADGES: Record<SupportTicketStatus, BadgeVariant> = {
  new: "warning",
  open: "default",
  waiting_on_customer: "secondary",
  on_hold: "secondary",
  solved: "success",
};

export const PRIORITY_LABELS: Record<SupportTicketPriority, string> = {
  urgent: "Urgent",
  high: "High",
  normal: "Normal",
  low: "Low",
};

export const PRIORITY_BADGES: Record<SupportTicketPriority, BadgeVariant> = {
  urgent: "destructive",
  high: "warning",
  normal: "secondary",
  low: "secondary",
};

export const TYPE_LABELS: Record<SupportTicketType, string> = {
  bug: "Bug",
  question: "Question",
  change_request: "Change request",
  access: "Access",
  other: "Other",
};

export const CHANNEL_LABELS: Record<SupportChannel, string> = {
  email: "Email",
  portal: "Workspace",
  tool: "Tool feedback",
  applet: "Mockup builder",
  console: "Logged by an employee",
  mcp: "AI client",
};

export const VIEW_LABELS: Record<SupportViewKey, string> = {
  mine: "Your unsolved tickets",
  unassigned: "Unassigned",
  open: "All unsolved",
  waiting_on_customer: "Waiting on customer",
  on_hold: "On hold",
  solved: "Solved",
};

export const REASON_LABELS = {
  unknown_sender: "Unknown sender",
  ambiguous_sender: "Sender belongs to more than one customer",
  automatic_reply: "Automatic reply",
  invalid_reply_token: "Reply to a ticket address that does not exist",
  unverified_sender: "Sender could not be verified",
} as const;

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "Just now", "12m ago", "3h ago", "Yesterday", or a date, from Unix seconds. */
export function formatWhen(seconds: number, now = Math.floor(Date.now() / 1000)): string {
  const age = Math.max(0, now - seconds);
  if (age < MINUTE) return "Just now";
  if (age < HOUR) return `${String(Math.floor(age / MINUTE))}m ago`;
  if (age < DAY) return `${String(Math.floor(age / HOUR))}h ago`;
  if (age < 2 * DAY) return "Yesterday";
  return new Date(seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function span(seconds: number): string {
  if (seconds < HOUR) return `${String(Math.max(1, Math.round(seconds / MINUTE)))}m`;
  if (seconds < DAY) return `${String(Math.round(seconds / HOUR))}h`;
  return `${String(Math.round(seconds / DAY))}d`;
}

/** When a reply is due, as "Reply due in 3h" or "Overdue by 20m"; null when nobody is waiting. */
export function replyDue(
  dueAt: number | null,
  now = Math.floor(Date.now() / 1000),
): { text: string; overdue: boolean } | null {
  if (dueAt === null) return null;
  return dueAt > now
    ? { text: `Reply due in ${span(dueAt - now)}`, overdue: false }
    : { text: `Overdue by ${span(now - dueAt)}`, overdue: true };
}

/** A full timestamp for a message header. */
export function formatTimestamp(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const text = (value: unknown) => (typeof value === "string" ? value : null);

const FIELD_LABELS: Partial<Record<string, Readonly<Record<string, string>>>> = {
  status: STATUS_LABELS,
  priority: PRIORITY_LABELS,
  type: TYPE_LABELS,
};

function fieldValue(field: string, value: unknown, names: ReadonlyMap<string, string>): string {
  const raw = text(value);
  if (raw === null) return "none";
  if (field === "assignee") return names.get(raw) ?? "another employee";
  return FIELD_LABELS[field]?.[raw] ?? raw;
}

function describeFieldChange(
  event: SupportEvent,
  field: string,
  names: ReadonlyMap<string, string>,
): string {
  const who = event.actorLabel;
  const to = fieldValue(field, event.payload["to"], names);
  if (field !== "assignee") return `${who} set the ${field} to ${to}`;
  return event.payload["reason"] === "first_reply"
    ? `${who} took the ticket by replying`
    : `${who} assigned the ticket to ${to}`;
}

function describeTags(event: SupportEvent): string {
  const list = (value: unknown) => (Array.isArray(value) ? value.map(String) : []);
  const added = list(event.payload["added"]);
  const removed = list(event.payload["removed"]);
  const parts = [
    added.length > 0 ? `added ${added.join(", ")}` : null,
    removed.length > 0 ? `removed ${removed.join(", ")}` : null,
  ].filter((part): part is string => part !== null);
  return `${event.actorLabel} ${parts.join(" and ")}`;
}

/** Where a portal, tool, or applet request was sent from. */
function describeRequestContext(event: SupportEvent): string | null {
  const name = event.payload["targetName"];
  const path = event.payload["shellPath"];
  return typeof name === "string" && typeof path === "string"
    ? `Sent from ${name} (${path})`
    : null;
}

const AUTOMATION_FAILURES: Readonly<Record<string, string>> = {
  gateway_not_configured: "the AI gateway is not set up",
  model_not_found: "the gateway does not know the model",
  gateway_rejected: "the gateway refused the request",
  gateway_error: "the AI gateway did not answer",
  malformed_output: "the model's answer was not usable",
  start_failed: "it could not start",
};

/** Why an automation run failed, in words (ADR-0089). */
export function automationFailureText(code: string | null): string {
  return AUTOMATION_FAILURES[code ?? ""] ?? "something went wrong";
}

function describeAutomationFailure(event: SupportEvent): string {
  const name = event.payload["automation"] === "triage" ? "AI triage" : "The AI draft reply";
  const code = event.payload["code"];
  return `${name} did not finish: ${automationFailureText(typeof code === "string" ? code : null)}`;
}

/**
 * One line for the thread describing an event, or `null` for events the thread already shows
 * another way (a message is its own card).
 */
export function describeEvent(
  event: SupportEvent,
  names: ReadonlyMap<string, string> = new Map(),
): string | null {
  const changed = /^ticket\.(status|priority|type|assignee|tool)_changed$/u.exec(event.type)?.[1];
  if (changed) return describeFieldChange(event, changed, names);
  if (event.type === "ticket.created") return `${event.actorLabel} opened the ticket`;
  if (event.type === "ticket.tags_changed") return describeTags(event);
  if (event.type === "draft.saved") return `${event.actorLabel} saved a draft reply`;
  if (event.type === "request.context") return describeRequestContext(event);
  if (event.type === "automation.failed") return describeAutomationFailure(event);
  return null;
}

/** Who asked: their name, else their address, else a plain placeholder. */
export function requesterLabel(requester: { name: string; email: string | null }): string {
  const name = requester.name.trim();
  if (name !== "") return name;
  return requester.email ?? "Unknown requester";
}

const MESSAGE_ACTIVITY: Readonly<Record<string, string>> = {
  customer: "wrote",
  reply: "replied",
  note: "added an internal note",
};

const DELIVERY_ACTIVITY: Readonly<Record<string, string>> = {
  "message.emailed": "The reply was emailed",
  "message.not_emailed": "The reply could not be emailed",
  "confirmation.failed": "The confirmation email could not be sent",
  "confirmation.skipped": "No confirmation was sent: this address got several in the last hour",
};

/** One line for the ticket's activity log: every change, message, and email, or null. */
export function describeActivity(
  event: SupportEvent,
  names: ReadonlyMap<string, string> = new Map(),
): string | null {
  const described = describeEvent(event, names);
  if (described !== null) return described;
  if (event.type === "message.added") {
    const verb = MESSAGE_ACTIVITY[text(event.payload["kind"]) ?? ""];
    return verb === undefined ? null : `${event.actorLabel} ${verb}`;
  }
  if (event.type === "draft.discarded") return `${event.actorLabel} discarded the draft reply`;
  return DELIVERY_ACTIVITY[event.type] ?? null;
}

/** A saved reply's text for this ticket: `{{first_name}}` becomes the requester's first name. */
export function personalize(text: string, ticket: { requester: { name: string } }): string {
  const first = /^\S+/u.exec(ticket.requester.name.trim())?.[0] ?? "there";
  return text.replaceAll("{{first_name}}", first);
}

/** Adds a saved reply's text to the reply being written, after what is already there. */
export function withSavedReply(current: string, text: string): string {
  return current.trim() === "" ? text : `${current.trimEnd()}\n\n${text}`;
}
